import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { CircleCheck, Star, PackageCheck, Calendar, Users, Truck, ChevronRight, Zap } from 'lucide-react';
import { listerEnAttente, surChangement } from '../utils/fileAttentePhotos';
import CarteProfessionnelle from './CarteProfessionnelle';

const isoLocal = (d) => {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const API = process.env.REACT_APP_BACKEND_URL || '';

const formaterJour = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso + 'T12:00:00');
  if (isNaN(d)) return iso;
  return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
};

const TechDashboard = () => {
  const [chantiers, setChantiers] = useState([]);
  const [chargement, setChargement] = useState(null);
  const [equipe, setEquipe] = useState(null);
  const [attente, setAttente] = useState([]);
  const [charge, setCharge] = useState(true);
  const navigate = useNavigate();

  const tout = async () => {
    try {
      const [rC, rCh, rE] = await Promise.all([
        axios.get(`${API}/api/chantiers/mes/liste`),
        axios.get(`${API}/api/chargements/mon-prochain`).catch(() => ({ data: { data: null } })),
        axios.get(`${API}/api/equipes/mon-equipe`).catch(() => ({ data: { data: null } }))
      ]);
      setChantiers(rC.data.data || []);
      setChargement(rCh.data?.data || null);
      setEquipe(rE.data?.data || null);
    } catch (e) {
    } finally {
      setCharge(false);
    }
  };

  useEffect(() => {
    tout();
    const t = setInterval(tout, 120000);
    const auRetour = () => { if (!document.hidden) tout(); };
    document.addEventListener('visibilitychange', auRetour);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', auRetour); };
  }, []);

  useEffect(() => {
    const maj = async () => setAttente(await listerEnAttente());
    maj();
    return surChangement(maj);
  }, []);

  const aujourdhui = isoLocal(new Date());
  const moisEnCours = aujourdhui.slice(0, 7);

  const terminesMois = chantiers.filter((c) =>
    c.etape === 'termine' && String(c.updated_at || '').slice(0, 7) === moisEnCours
  ).length;

  const prochains = chantiers
    .filter((c) => c.etape !== 'termine' && c.date_prevue >= aujourdhui)
    .sort((a, b) => (a.date_prevue || '').localeCompare(b.date_prevue || ''))
    .slice(0, 2);

  const carte = { background: '#fff', borderRadius: '14px', padding: '18px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: '14px' };
  const titre = { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#6B7280', marginBottom: '10px' };
  const valeur = { fontSize: '28px', fontWeight: 600, color: '#111827', lineHeight: 1 };

  if (charge) return <div style={{ padding: '24px', textAlign: 'center', color: '#6B7280' }}>Chargement…</div>;

  return (
    <div style={{ padding: '8px', maxWidth: '640px', margin: '0 auto', width: '100%' }}>

      {attente.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#FEF3C7', color: '#92400E', padding: '13px 15px', borderRadius: '12px', marginBottom: '14px', fontSize: '14px' }}>
          <PackageCheck size={17} />
          {attente.length} photo{attente.length > 1 ? 's' : ''} en attente d'envoi
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
        <div style={{ ...carte, marginBottom: 0 }}>
          <div style={titre}><CircleCheck size={15} color="#10B981" /> Terminés</div>
          <div style={valeur}>{terminesMois}</div>
          <div style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '3px' }}>ce mois-ci</div>
        </div>

        <div style={{ ...carte, marginBottom: 0 }}>
          <div style={titre}><Star size={15} color="#10B981" /> Ma note</div>
          <div style={{ ...valeur, color: '#9CA3AF' }}>—</div>
          <div style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '3px' }}>bientôt</div>
        </div>
      </div>

      <div style={carte}>
        <div style={titre}><Truck size={15} color="#10B981" /> Prochain chargement</div>
        {chargement ? (
          <>
            <div style={{ fontSize: '19px', fontWeight: 600, textTransform: 'capitalize', marginBottom: '4px' }}>
              {formaterJour(chargement.date)}
            </div>
            <div style={{ fontSize: '15px', color: '#10B981' }}>à {chargement.heure} au dépôt</div>
          </>
        ) : (
          <div style={{ fontSize: '15px', color: '#9CA3AF' }}>Aucun chargement programmé</div>
        )}
      </div>

      <div style={carte}>
        <div style={titre}><Calendar size={15} color="#10B981" /> Prochain{prochains.length > 1 ? 's' : ''} chantier{prochains.length > 1 ? 's' : ''}</div>
        {prochains.length === 0 ? (
          <div style={{ fontSize: '15px', color: '#9CA3AF' }}>Aucun chantier à venir</div>
        ) : prochains.map((p, i) => (
          <div key={p.id}
            onClick={() => navigate('/ops/mon-planning', { state: { jour: p.date_prevue } })}
            style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px',
              cursor: 'pointer',
              paddingTop: i > 0 ? '14px' : 0,
              paddingBottom: i < prochains.length - 1 ? '14px' : 0,
              borderBottom: i < prochains.length - 1 ? '1px solid #F3F4F6' : 'none'
            }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '16px', fontWeight: 600, textTransform: 'capitalize', marginBottom: '5px' }}>
                {formaterJour(p.date_prevue)}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#6B7280', marginBottom: '3px' }}>
                <Zap size={14} />
                {p.puissance_kwc ? `${p.puissance_kwc} kWc` : '—'}
                {p.nb_panneaux ? ` · ${p.nb_panneaux} panneaux` : ''}
              </div>
              <div style={{ fontSize: '13px', color: '#6B7280' }}>
                Département {String(p.client?.code_postal || '').slice(0, 2) || '—'}
              </div>
            </div>
            <ChevronRight size={18} color="#9CA3AF" style={{ flexShrink: 0, marginTop: '3px' }} />
          </div>
        ))}
      </div>

      <div style={carte}>
        <div style={titre}><Users size={15} color="#10B981" /> Mon équipe</div>
        {equipe ? (
          <>
            <div style={{ fontSize: '17px', fontWeight: 600, marginBottom: '8px' }}>{equipe.nom}</div>
            {(equipe.membres || []).map((m) => (
              <div key={m.id} style={{ fontSize: '14px', color: '#374151', marginBottom: '3px' }}>
                {m.prenom} {m.nom}{m.chef ? ' · chef d\u2019équipe' : ''}
              </div>
            ))}
            {(equipe.camion_nom || equipe.camion_immat) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #F3F4F6', fontSize: '13px', color: '#6B7280' }}>
                <Truck size={14} /> {equipe.camion_nom} {equipe.camion_immat ? `· ${equipe.camion_immat}` : ''}
              </div>
            )}
          </>
        ) : (
          <div style={{ fontSize: '15px', color: '#9CA3AF' }}>Aucune équipe assignée</div>
        )}
      </div>

      <CarteProfessionnelle />
    </div>
  );
};

export default TechDashboard;
